import { Button } from 'components/ui/button';
import Voices from './Voices';
import VoiceSkeletonLoader from './VoiceSkeletonLoader';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { useVoices } from '../hooks/useVoices';

export const VoicePanelMobile = ({
  data,
  selectedName,
  setSelectedName,
}: {
  data: any;
  selectedName: string;
  setSelectedName: (name: string) => void;
}) => {
  const [activeTab, setActiveTab] = useState('female');

  return (
    <div className="space-y-4">
      <div className="flex rounded-lg bg-background p-1">
        <button
          className={`flex-1 rounded-md py-1 text-sm transition-colors ${activeTab === 'female' ? 'bg-primary text-white shadow-sm' : 'text-text-primary'}`}
          onClick={() => setActiveTab('female')}
        >
          Female
        </button>
        <button
          className={`flex-1 rounded-md py-1 text-sm transition-colors ${activeTab === 'male' ? 'bg-primary text-white shadow-sm' : 'text-text-primary'}`}
          onClick={() => setActiveTab('male')}
        >
          Male
        </button>
      </div>
      <div className="space-y-2">
        {activeTab === 'female' && (
          <Voices
            names={data?.female || []}
            selectedName={selectedName}
            setSelectedName={setSelectedName}
            title="FEMALE"
          />
        )}
        {activeTab === 'male' && (
          <Voices
            names={data?.male || []}
            selectedName={selectedName}
            setSelectedName={setSelectedName}
            title="MALE"
          />
        )}
      </div>
    </div>
  );
};

interface EditToolProps {
  text: string;
  lastProcessedText: string;
  setLastProcessedText: (text: string) => void;
  handleGenerate: (
    e: React.MouseEvent,
    text: string,
    selectedName: string
  ) => void;
  selectedName: string;
  setSelectedName: (name: string) => void;
  setIsChanged: (name: string) => void;
  isChanged: string;
}

export default function EditTool({
  text,
  lastProcessedText,
  handleGenerate,
  selectedName,
  setSelectedName,
  setLastProcessedText,
  setIsChanged,
  isChanged,
}: EditToolProps) {
  const { data, isLoading } = useVoices();
  const [isVoicePanelOpen, setIsVoicePanelOpen] = useState(false);

  if (isLoading) return <VoiceSkeletonLoader />;

  // Check if there are actual changes in text or voice
  const hasVoiceChanged = selectedName !== isChanged;
  const hasTextChanged = text !== lastProcessedText;
  const hasValidVoice = selectedName !== '';
  const hasValidText = text.length > 0;

  // Button is enabled only when there's valid input and actual changes
  const isButtonEnabled =
    hasValidVoice && hasValidText && (hasVoiceChanged || hasTextChanged);

  const handleButtonClick = (e: React.MouseEvent) => {
    if (handleGenerate && text && selectedName) {
      handleGenerate(e, text, selectedName);
      setLastProcessedText(text);
      setIsChanged(selectedName);
    } else {
      toast.error('Please Type Text and Select a Voice');
    }
  };

  return (
    <div className="bg-background-main flex flex-col rounded-md p-2 md:p-3 px-3 md:px-4 w-full md:w-[328px]">
      <p className="text-text-primary uppercase text-sm mb-2">Assign Voice</p>

      {/* Mobile View */}
      <div className="lg:hidden">
        <Button
          variant="customPrimary"
          className="w-full text-[14px] mb-4 bg-primary"
          onClick={() => setIsVoicePanelOpen(!isVoicePanelOpen)}
        >
          {isVoicePanelOpen ? 'Hide Voices' : 'Assign Voice'}
        </Button>
        {isVoicePanelOpen && (
          <VoicePanelMobile
            data={data}
            selectedName={selectedName}
            setSelectedName={setSelectedName}
          />
        )}
        {selectedName && (
          <Button
            variant="customPrimary"
            className="mb-7 w-full text-[14px] mt-auto bg-primary"
            onClick={handleButtonClick}
            disabled={!isButtonEnabled}
          >
            Regenerate Audio
          </Button>
        )}
      </div>

      {/* Desktop View */}
      <div className="hidden lg:flex h-full flex-col">
        <div>
          <Voices
            names={data?.female || []}
            selectedName={selectedName}
            setSelectedName={setSelectedName}
            title="FEMALE"
          />
          <Voices
            names={data?.male || []}
            selectedName={selectedName}
            setSelectedName={setSelectedName}
            title="MALE"
          />
        </div>
        <Button
          variant="customPrimary"
          className="mb-7 w-full mt-auto bg-primary"
          onClick={handleButtonClick}
          disabled={!isButtonEnabled}
        >
          Regenerate Audio
        </Button>
      </div>
    </div>
  );
}
